'use strict';

/**
 * Bootstrap marketing modal after shell loads. Loads CSS/JS from bootstrap `assets` before injecting HTML
 * so `.rpm-pm` is styled (no PageView / critical-CSS ordering dependency).
 */

const STORAGE_PREFIX = 'rpm_promotion_modal_';

/**
 * @param {string} key
 * @returns {{ ids: number[] }}
 */
function readState(key) {
	try {
		const raw = window.localStorage.getItem(key);
		if (!raw) {
			return { ids: [] };
		}
		const parsed = JSON.parse(raw);
		if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.ids)) {
			return { ids: [] };
		}
		const ids = [];
		for (let i = 0; i < parsed.ids.length; i += 1) {
			const v = parsed.ids[i];
			if (typeof v === 'number' && Number.isFinite(v)) {
				ids.push(v);
			} else if (typeof v === 'string' && /^\d+$/.test(v)) {
				ids.push(Number.parseInt(v, 10));
			}
		}
		return { ids };
	} catch (e) {
		return { ids: [] };
	}
}

/**
 * @param {string} key
 * @param {number} modalId
 */
function rememberModalId(key, modalId) {
	const state = readState(key);
	if (state.ids.indexOf(modalId) === -1) {
		state.ids.push(modalId);
	}
	try {
		window.localStorage.setItem(key, JSON.stringify(state));
	} catch (e) {
		/* ignore quota */
	}
}

/**
 * @param {string[]} urls
 * @returns {Promise<void>}
 */
function loadStyles(urls) {
	return urls.reduce(
		(chain, href) => chain.then(
			() => new Promise((resolve, reject) => {
				const link = document.createElement('link');
				link.rel = 'stylesheet';
				link.href = href;
				link.onload = () => resolve();
				link.onerror = () => reject(new Error('stylesheet ' + href));
				document.head.appendChild(link);
			}),
		),
		Promise.resolve(),
	);
}

/**
 * @param {string} src
 * @returns {Promise<void>}
 */
function loadScript(src) {
	return new Promise((resolve, reject) => {
		const s = document.createElement('script');
		s.src = src;
		s.async = true;
		s.onload = () => resolve();
		s.onerror = () => reject(new Error('script ' + src));
		document.body.appendChild(s);
	});
}

function run() {
	if (!window.rp_app || !window.rp_app.data || !window.rp_app.data.promotion_modal) {
		return;
	}

	const host = document.getElementById('rp-promotion-modal-host');
	if (!host || typeof window.rp_app.request !== 'function') {
		return;
	}

	const mainEl = document.querySelector('.main');
	const fromMain = mainEl !== null ? mainEl.getAttribute('data-store-hash') : null;
	const storeHash = document.body.getAttribute('data-store-hash') || fromMain || 'default';
	const key = STORAGE_PREFIX + storeHash;
	const state = readState(key);

	window.__rpmOnPromotionModalDismiss = function (modalId) {
		rememberModalId(key, modalId);
	};

	const strings = window.rp_app.data.promotion_modal;
	const defaultSubmitError = (typeof strings.error_generic === 'string' && strings.error_generic !== '')
		? strings.error_generic
		: 'Request could not be completed.';

	const body = JSON.stringify({ client_state: { ids: state.ids } });

	window.rp_app.request({
		method: 'POST',
		url: '/ajax/promotion-modal/bootstrap',
		contentType: 'application/json; charset=UTF-8',
		dataType: 'json',
		data: body,
		success(resp) {
			if (!resp || typeof resp !== 'object') {
				return;
			}
			if (resp.client_state && typeof resp.client_state === 'object' && Array.isArray(resp.client_state.ids)) {
				try {
					window.localStorage.setItem(key, JSON.stringify(resp.client_state));
				} catch (e) {
					/* ignore */
				}
			}
			if (!resp.show || !resp.modal_html || !resp.modal_id) {
				return;
			}
			const modalId = resp.modal_id;
			const assets = resp.assets && typeof resp.assets === 'object' ? resp.assets : {};
			const cssUrls = Array.isArray(assets.css) ? assets.css : [];
			const jsUrls = Array.isArray(assets.js) ? assets.js : [];
			const needsCss = cssUrls.length > 0;
			const cssPromise = needsCss ? loadStyles(cssUrls) : Promise.resolve();

			cssPromise.then(() => {
				host.innerHTML = resp.modal_html;
				const root = host.querySelector('[data-rpm-pm-root]');
				if (!root) {
					host.innerHTML = '';
					return;
				}

				const successUi = resp.success_ui && typeof resp.success_ui === 'object' ? resp.success_ui : {};

				const attach = () => {
					if (typeof window.__rpmAttachPromotionModal !== 'function') {
						return false;
					}
					window.__rpmAttachPromotionModal(root, modalId, successUi, defaultSubmitError);
					return true;
				};

				const teardownFailedInject = () => {
					host.innerHTML = '';
				};

				if (jsUrls.length === 0) {
					if (!attach()) {
						teardownFailedInject();
					}
					return;
				}

				jsUrls
					.reduce(
						(p, src) => p.then(() => loadScript(src)),
						Promise.resolve(),
					)
					.then(() => {
						if (!attach()) {
							teardownFailedInject();
						}
					})
					.catch(() => {
						teardownFailedInject();
					});
			}).catch(() => {
				if (needsCss) {
					/* Required stylesheet(s) failed; do not show an unstyled modal. */
				}
			});
		},
	});
}

module.exports = run;
